import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { LuCirclePlus, LuArrowRight } from "react-icons/lu";
import { getFirestore, collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { app } from "../firebase";
import AgendaPage from "../components/Agenda";
import { useNavigate } from "react-router-dom";
import LoadingComLayout from "../components/LoadingComLayout";
import ModalAgendar from "../components/ModalAgendar";

const db = getFirestore(app);

/* ======================= ESTILOS RESPONSIVOS ======================= */
const Main = styled.main`
  flex: 1;
  padding: 2rem 4rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2rem;
  background-color: #f3f7ff;
  min-height: 100vh;

  @media (max-width: 1280px) { padding: 2rem; }
  @media (max-width: 768px) { padding: 1rem; gap: 1.25rem; }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: .75rem;

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: #1F1A40;

  @media (max-width: 1024px) { font-size: 1.75rem; }
  @media (max-width: 640px)  { font-size: 1.5rem; }
`;

const CardsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  padding: 1.5rem;
  border-radius: 1.5rem;
  background: linear-gradient(
    135deg,
    ${(p) => p.cor1 || '#3a79ed'},
    ${(p) => p.cor2 || '#3a79ed'}
  );
  color: ${(p) => p.texto || 'white'};
  min-height: 150px;
  box-shadow: 0 4px 16px rgba(0,0,0,.05);

  h3 {
    font-size: 1rem;
    font-weight: 600;
    opacity: .95;
    margin: 0 0 .5rem 0;
  }
`;

const StatValue = styled.span`
  font-size: 3rem;
  line-height: 1.1;
  font-weight: 800;

  @media (max-width: 1024px) { font-size: 2.4rem; }
  @media (max-width: 640px)  { font-size: 2rem; }
`;

const MoneyValue = styled.span`
  font-size: 2.6rem;
  line-height: 1.1;
  font-weight: 800;

  @media (max-width: 1024px) { font-size: 2.2rem; }
  @media (max-width: 640px)  { font-size: 1.9rem; }
`;

const CardFooter = styled.div`
  margin-top: auto;
  display: flex;
  justify-content: flex-end;

  a {
    display: inline-flex;
    align-items: center;
    gap: .4rem;
    font-size: .95rem;
    color: currentColor;
    text-decoration: none;
  }
`;

const AgendaWrapper = styled.section`
  background: #fff;
  border-radius: 1rem;
  padding: 0; /* caso a Agenda já tenha padding próprio */
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0,0,0,.05);
`;

/* Botão existe no arquivo original, mas não é usado aqui.
   Mantive o estilo para compatibilidade futura. */
const Botao = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.95rem;
  color: #fff;
  background-color: #3a79ed;
  box-shadow: 0 4px 12px rgba(58, 136, 237, 0.3);
  font-weight: 600;
  cursor: pointer;

  @media (max-width: 560px) {
    width: 100%;
    justify-content: center;
  }
`;

export default function BarbeiroPainel() {
  const [user, setUser] = useState(null);
  const [barbeariaId, setBarbeariaId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agendamentosHoje, setAgendamentosHoje] = useState(0);
  const [comissaoHoje, setComissaoHoje] = useState(0);
  const [modalAberto, setModalAberto] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();

  // timezone helper — retorna 'YYYY-MM-DD' em America/Sao_Paulo
  const TZ = 'America/Sao_Paulo';
  function todayISO_BR(date = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const y = parts.find(p => p.type === 'year').value;
    const m = parts.find(p => p.type === 'month').value;
    const d = parts.find(p => p.type === 'day').value;
    return `${y}-${m}-${d}`; // ex: 2025-08-11
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/login");
        return;
      }

      setUser(currentUser);
      const uid = currentUser.uid;

      const barbeariasSnapshot = await getDocs(collection(db, "barbearia"));
      for (const docBarbearia of barbeariasSnapshot.docs) {
        const barbeiroRef = doc(db, `barbearia/${docBarbearia.id}/barbeiros/${uid}`);
        const barbeiroSnap = await getDoc(barbeiroRef);

        if (barbeiroSnap.exists()) {
          setBarbeariaId(docBarbearia.id);

          await buscarAgendamentosHoje(docBarbearia.id, uid);
          await calcularComissaoHoje(docBarbearia.id, uid, barbeiroSnap.data().comissao || 0);
          break;
        }
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const buscarAgendamentosHoje = async (barbeariaId, barbeiroId) => {
    const hoje = todayISO_BR();

    const qAg = query(
      collection(db, `barbearia/${barbeariaId}/agendamentos`),
      where("data", "==", hoje),
      where("status", "==", "confirmado"),
      where("barbeiroId", "==", barbeiroId)
    );

    const snapshot = await getDocs(qAg);
    setAgendamentosHoje(snapshot.size);
  };

  const calcularComissaoHoje = async (barbeariaId, barbeiroId, percentual) => {
    const hoje = todayISO_BR();

    const servicosSnap = await getDocs(collection(db, `barbearia/${barbeariaId}/servico`));
    const mapaServicos = {};
    servicosSnap.forEach(doc => {
      mapaServicos[doc.id] = doc.data().valor || 0;
    });

    const agendamentosSnap = await getDocs(query(
      collection(db, `barbearia/${barbeariaId}/agendamentos`),
      where("data", "==", hoje),
      where("status", "==", "confirmado"),
      where("barbeiroId", "==", barbeiroId)
    ));

    let total = 0;
    agendamentosSnap.forEach(doc => {
      const ag = doc.data();
      const valor = mapaServicos[ag.servicoId] || 0;
      total += (valor * percentual) / 100;
    });

    setComissaoHoje(total);
  };

  function formatarValor(valor) {
    return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  }

  if (loading) return <LoadingComLayout />;

  return (
    <>
      <>
        <Main>
          <CardsGrid>
            <Card texto="white">
              <h3>Agendamentos Hoje</h3>
              <StatValue>{agendamentosHoje}</StatValue>
              <CardFooter>
                <a href="#">
                  <LuArrowRight /> Ver mais
                </a>
              </CardFooter>
            </Card>

            <Card cor1="#e1ecff" cor2="#e1ecff" texto="#1F1A40">
              <h3>Comissão Prevista de Hoje</h3>
              <MoneyValue>{formatarValor(comissaoHoje)}</MoneyValue>
              <CardFooter>
                <a href="/comissao">
                  <LuArrowRight /> Detalhes
                </a>
              </CardFooter>
            </Card>
          </CardsGrid>

          <AgendaWrapper>
            <AgendaPage />
          </AgendaWrapper>
        </Main>
      </>

      {modalAberto && barbeariaId && (
        <ModalAgendar barbeariaId={barbeariaId} onClose={() => setModalAberto(false)} />
      )}
    </>
  );
}
